import { Injectable } from '@angular/core';
import type { Stripe, StripePaymentElementOptions } from '@stripe/stripe-js';
import { environment } from 'environments/environment';

/**
 * Embedded Stripe Checkout. The publishable key should come from the API (`stripe_publishable_key`);
 * the environment value is an optional dev fallback.
 *
 * The `@stripe/stripe-js` runtime is loaded only via dynamic `import()` inside {@link getStripe}, so
 * Stripe network calls (e.g. m.stripe.com) run only when a flow mounts the Payment Element
 * (billing / `update_credit_card` and register payment step).
 */
@Injectable({ providedIn: 'root' })
export class StripeEmbeddedService {
  private readonly stripeByKey = new Map<string, Promise<Stripe | null>>();

  private resolvePublishableKey(explicit: string | undefined | null): string {
    return (
      (explicit && explicit.trim()) ||
      (environment as any).stripePublishableKey ||
      ''
    );
  }

  /** Lazy-load Stripe.js once per publishable key (async chunk; no load until first call). */
  getStripe(publishableKey: string | null | undefined): Promise<Stripe | null> {
    const key = this.resolvePublishableKey(publishableKey);
    if (!key) {
      return Promise.resolve(null);
    }
    if (!this.stripeByKey.has(key)) {
      this.stripeByKey.set(
        key,
        import('@stripe/stripe-js').then(({ loadStripe }) => loadStripe(key)),
      );
    }
    return this.stripeByKey.get(key)!;
  }

  /**
   * Mount embedded Checkout in `container`. `publishableKey` from API is preferred.
   */
  async initEmbeddedCheckout(
    clientSecret: string,
    container: HTMLElement,
    publishableKey: string | null | undefined,
  ): Promise<{ destroy: () => void } | null> {
    const stripe = await this.getStripe(publishableKey);
    if (!stripe || !(stripe as any).initEmbeddedCheckout) {
      return null;
    }
    const checkout = await (stripe as any).initEmbeddedCheckout({
      clientSecret,
    });
    await checkout.mount(container);
    return { destroy: () => checkout.destroy() };
  }

  /**
   * Payment Element for SetupIntent (add card, wallets, bank methods per Dashboard).
   */
  async mountPaymentElementForSetupIntent(
    clientSecret: string,
    container: HTMLElement,
    publishableKey: string | null | undefined,
    onReady?: () => void,
  ): Promise<{
    destroy: () => void;
    confirm: () => Promise<{ error?: unknown; setupIntent?: { id?: string; status?: string } }>;
  } | null> {
    const stripe = await this.getStripe(publishableKey);
    if (!stripe) {
      return null;
    }
    const elements = stripe.elements({
      clientSecret,
      appearance: { theme: 'stripe' },
    });
    // Wallets must be allowed here (auto) *and* enabled in Dashboard. They render as
    // express buttons above the card fields, not as extra “tabs”.
    // `link` is supported at runtime but missing from PaymentWalletsOption in @stripe/stripe-js@1.54.
    const paymentElement = elements.create('payment', {
      layout: 'tabs',
      wallets: {
        applePay: 'auto',
        googlePay: 'auto',
        link: 'never',
      },
    } as StripePaymentElementOptions);
    let readyFired = false;
    if (onReady) {
      paymentElement.on('ready', () => {
        readyFired = true;
        onReady();
      });
      // Fallback: some edge cases may not fire 'ready' (or fire before handler wiring in older versions).
      setTimeout(() => {
        if (!readyFired) {
          onReady();
        }
      }, 1500);
    }
    paymentElement.mount(container);
    return {
      destroy: () => {
        try {
          paymentElement.destroy();
        } catch {
          /* noop */
        }
      },
      confirm: () =>
        stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        }),
    };
  }
}
